import * as React from 'react';
import { faFaceAnxiousSweat } from '@fortawesome/pro-duotone-svg-icons';
import { IconButton, styled } from '@mend/mui';
import { DarkMode, LightMode } from '@mui/icons-material';
import { Link, Tooltip, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { isRouteErrorResponse, Outlet, useLocation, useRouteError } from 'react-router';

import { config } from '#config.ts';
import { browserAgent } from '#lib/new-relic.ts';
import { me } from '#lib/react-query/queries.ts';
import { getAccessToken } from '#utils/cookies.ts';
import { isTokenValid } from '#utils/jwt.ts';
import logoDarkUrl from '../assets/logo-dark.svg';
import logoLightUrl from '../assets/logo-light.svg';
import {
  Description as StatusMessageDescription,
  Root as StatusMessageRoot,
} from '../components/StatusMessage.tsx';
import { useThemeMode } from '../lib/ThemeProvider.tsx';

const DebugSeederFab = React.lazy(() =>
  import('../components/DebugSeeder/DebugSeederFab.tsx').then((module) => ({
    default: module.DebugSeederFab,
  }))
);

const StyledNav = styled('nav')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(4, 3),
  boxShadow: theme.shadows[3],
  // One-off colors
  backgroundColor: theme.palette.mode === 'dark' ? '#001923de' : '#fffffff2',
}));

const Logo = styled('img')({
  display: 'block',
});

const Content = styled('main')(({ theme }) => ({
  // The `104px` is the height of the nav bar, if it changes, this needs to be
  // updated.
  height: 'calc(100% - 104px)',
  padding: theme.spacing(3),
}));

function Nav() {
  const theme = useTheme();
  const { toggle } = useThemeMode();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <StyledNav>
      <Logo src={isDarkMode ? logoDarkUrl : logoLightUrl} height={40} alt="Mend Signal" />

      <Tooltip title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}>
        <IconButton onClick={() => toggle()} color="inherit" size="small">
          {isDarkMode ? <LightMode /> : <DarkMode />}
        </IconButton>
      </Tooltip>
    </StyledNav>
  );
}

function AdminDebugSeederGate() {
  const hasValidAccessToken = isTokenValid(getAccessToken());
  const meQuery = useQuery({ ...me, enabled: hasValidAccessToken });

  if (!hasValidAccessToken || meQuery.status !== 'success' || meQuery.data.user.role !== 'Admin') {
    return null;
  }

  return (
    <React.Suspense fallback={null}>
      <DebugSeederFab />
    </React.Suspense>
  );
}

export function Layout() {
  return (
    <>
      <Nav />

      <Content>
        <Outlet />
      </Content>

      <AdminDebugSeederGate />
    </>
  );
}

interface BaseErrorMetadata {
  route: string;
}

interface RouterResponseErrorMetadata extends BaseErrorMetadata {
  type: 'route.response-error';
  status: number;
  statusText: string;
}

interface RenderErrorMetadata extends BaseErrorMetadata {
  type: 'react.render-error';
  message: string;
  stack?: string;
}

interface UnknownErrorMetadata extends BaseErrorMetadata {
  type: 'unknown-error';
  originalError: string;
}

export function LayoutError() {
  const location = useLocation();
  const error = useRouteError();

  React.useEffect(() => {
    if (!error) return;

    let err: Error;
    let errData: RouterResponseErrorMetadata | RenderErrorMetadata | UnknownErrorMetadata;

    if (isRouteErrorResponse(error)) {
      err = new Error(`Route error: ${error.status} ${error.statusText} ${error.data}`);
      errData = {
        type: 'route.response-error',
        route: location.pathname + location.search,
        status: error.status,
        statusText: error.statusText,
      };
    } else if (error instanceof Error) {
      err = error;
      errData = {
        type: 'react.render-error',
        route: location.pathname + location.search,
        message: error.message,
        stack: error.stack,
      };
    } else {
      err = new Error('Unknown error type caught in route');
      let originalError: string;
      try {
        originalError = JSON.stringify(error);
      } catch {
        originalError = 'Unserializable thrown value';
      }
      errData = {
        type: 'unknown-error',
        route: location.pathname + location.search,
        originalError,
      };
    }

    browserAgent.noticeError(err, errData);
  }, [error, location]);

  return (
    <>
      <Nav />

      <Content>
        <StatusMessageRoot
          title="Something went wrong"
          description={
            <StatusMessageDescription>
              Please try refreshing the page, or reach out to our support team at{' '}
              <Link href={`mailto:${config.SUPPORT_EMAIL}`}>{config.SUPPORT_EMAIL}</Link> if it
              continues.
            </StatusMessageDescription>
          }
          faIcon={faFaceAnxiousSweat}
        />
      </Content>
    </>
  );
}
